import { Container } from '@acies/core';
import { findGeometryParameter } from '../../../domain/configurations';
import type { IGeometryShapeType } from '../../../domain/geometry-shape-types';
import { GEOMETRY_MEASUREMENTS, type IGeometryMeasurement, measurementsOf } from '../../../domain/geometry-shapes';
import { ConfiguratorModel } from '../configurator.model';
import GeometryMeasurementInput from './geometry-measurement-input';

/** What each measurement is called, as the user reads it. A Record, so a measurement the domain gains is one this does not compile without a name for. */
const MEASUREMENT_TEXTS: Readonly<Record<IGeometryMeasurement, string>> = {
	leftHeight: 'Venstre højde',
	leftRadius: 'Venstre radius',
	leftWidth: 'Venstre bredde',
	rightHeight: 'Højre højde',
	rightRadius: 'Højre radius',
	rightWidth: 'Højre bredde',
	rise: 'Pilhøjde',
};

/**
 * Which way each measurement runs, as the icon in its field: across for a width, up for a height.
 *
 * A radius is shown as running across, and a rise as running up. A Record for the reason {@link MEASUREMENT_TEXTS} is one.
 */
const MEASUREMENT_ICONS: Readonly<Record<IGeometryMeasurement, GeometryMeasurementInput['icon']>> = {
	leftHeight: 'spacing-height-01',
	leftRadius: 'spacing-width-01',
	leftWidth: 'spacing-width-01',
	rightHeight: 'spacing-height-01',
	rightRadius: 'spacing-width-01',
	rightWidth: 'spacing-width-01',
	rise: 'spacing-height-01',
};

/**
 * The measurements of the shape the position is cut to, one field each, in the order the engine lists them.
 *
 * One field for every measurement any shape has, made once and shown or hidden, rather than fields made for the shape and thrown away with it: the fields are where the user will type a measurement, and a field rebuilt each time the model answers would be torn down under the cursor. Hidden ones are out of the layout,
 * so the ones on show stack from the top. A shape with no measurements leaves the column empty.
 *
 * The measurements are the model's: the geometry parameter is found among `ConfiguratorModel.currentParameters` here, the way `GeometrySelectList` finds it, rather than handed in by the dialog. What the dialog hands in is the one thing the model cannot say, which shape the shapes beside the column show.
 *
 * Shown only while the shapes beside it show the shape the position is cut to — see {@link selectedShape}. A shape the user has just picked has no measurements until the engine has worked them out, and one it refused has none at all; the measurements on hand are the old shape's, and shown beside a tile of another shape
 * they would say that shape has them.
 */
export default class GeometryMeasurements extends Container {
	/**
	 * The shape the shapes beside the column show selected, which the dialog hands on as the list says it.
	 *
	 * Null until the list has selected one, and while it is null nothing is shown: there is no telling yet whether the list agrees with the model.
	 */
	public set selectedShape(value: IGeometryShapeType | null) {
		this.#selectedShape = value;
		this.#showMeasurements();
	}

	public get selectedShape(): IGeometryShapeType | null {
		return this.#selectedShape;
	}

	#fields: Map<IGeometryMeasurement, GeometryMeasurementInput>;
	#selectedShape: IGeometryShapeType | null;

	public constructor() {
		super();
		this.#fields = new Map();
		this.#selectedShape = null;
		this.width = 160;
		this.height = 'fill';
		this.autoLayout = 'vertical';
		this.gapVertical = 16;
		// the listener is registered on the model, so it would otherwise run in its scope rather than
		// in this one
		this.parametersChanged = this.parametersChanged.bind(this);
		// Subscribed here rather than on connect, because an ObservableCollection tells its listeners
		// about a change and not about what it already holds. What it already holds is taken straight
		// after, so a column built after a position was selected has its measurements all the same.
		ConfiguratorModel.currentParameters.subscribe(this.parametersChanged);
		this.parametersChanged();
	}

	/**
	 * The field for that measurement, made the first time it is asked for.
	 *
	 * Made hidden, and told which measurement it is, named and given its icon once, since a field only ever shows the one measurement it was made for.
	 */
	public field(measurement: IGeometryMeasurement): GeometryMeasurementInput {
		let field = this.#fields.get(measurement);
		if (!field) {
			field = new GeometryMeasurementInput();
			field.measurement = measurement;
			field.text = MEASUREMENT_TEXTS[measurement];
			field.icon = MEASUREMENT_ICONS[measurement];
			field.visible = false;
			this.#fields.set(measurement, field);
		}
		return field;
	}

	/** Every field, in the order the engine lists the measurements, which is the order they stack in. */
	protected override initialize(): void {
		super.initialize();
		this.addComponents(GEOMETRY_MEASUREMENTS.map((measurement) => this.field(measurement)));
	}

	/**
	 * Unsubscribes, because the model outlives the column.
	 *
	 * `ConfiguratorModel` is a module constant, so a listener it still holds is a column that has been removed and cannot be collected.
	 */
	protected override disconnected(): void {
		super.disconnected();
		ConfiguratorModel.currentParameters.unsubscribe(this.parametersChanged);
	}

	/** Follows the geometry of the position being worked on, which is what the measurements are read from. */
	private parametersChanged(): void {
		this.#showMeasurements();
	}

	/**
	 * Shows the fields of the measurements the position's shape has, each holding its length, and hides the rest.
	 *
	 * None while there is no geometry, and none while the shapes beside the column show another shape than the position is cut to. The model is read each time rather than remembered, so the parameters and the selected shape settle it the same way whichever arrives last.
	 */
	#showMeasurements(): void {
		const geometry = findGeometryParameter(ConfiguratorModel.currentParameters.collection);
		const measured = geometry !== null && geometry.shapeType === this.#selectedShape ? measurementsOf(geometry.value) : [];
		for (const measurement of GEOMETRY_MEASUREMENTS) {
			const field = this.field(measurement);
			const length = measured.find((each) => each.measurement === measurement);
			field.visible = length !== undefined;
			if (length !== undefined) {
				field.value = length.value;
			}
		}
	}
}
customElements.define('cf-geometry-measurements', GeometryMeasurements);
